/**
 * useRosterData - Main data management hook for Roster feature
 * 
 * This hook manages all data loading for the Roster component with progressive enhancement.
 * It follows the same pattern as the ForecastVsActuals useForecastData hook.
 * 
 * Key responsibilities:
 * - Load roster data with progressive enhancement
 * - Manage loading states and error handling
 * - Handle data caching and refresh
 * - Coordinate between different data sources
 * - Provide clean data interface to components
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { RosterDataService } from '../services/RosterDataService';
import { clearStaffingCache, clearSpendCache } from '../../../api/api';
import { isCacheClearNeeded, markCachesCleared } from '../../../utils/cacheInvalidation';

// Create singleton service instance
const rosterDataService = new RosterDataService();

export function useRosterData(year = 2026, isActive = true, department = null) {
  // Core data state
  const [roster, setRoster] = useState([]);
  const [outcomeAllocations, setOutcomeAllocations] = useState([]);
  const [unassignedRoles, setUnassignedRoles] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [companyOptions, setCompanyOptions] = useState(['CA', 'US', 'Other']);
  const [locationOptions, setLocationOptions] = useState([
    { code: 'US', display: 'United States' },
    { code: 'CA', display: 'Canada' }
  ]);
  
  // Loading and error state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [basicRosterReady, setBasicRosterReady] = useState(false);
  
  // Progressive enhancement states
  const [enhancementProgress, setEnhancementProgress] = useState({
    allocations: 'pending', // pending, loading, complete, failed
    unassignedRoles: 'pending',
    supportingData: 'pending'
  });
  
  // Refs for cleanup and state management
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const yearRef = useRef(year);
  
  // Update year ref when year changes
  useEffect(() => {
    yearRef.current = year;
  }, [year]);
  
    // Cleanup on unmount or when becoming inactive
  useEffect(() => {
    if (!isActive) {
      console.log('📊 useRosterData: Component became inactive, stopping any ongoing operations');
      loadingRef.current = false;
    }
    
    return () => {
      mountedRef.current = false;
      loadingRef.current = false;
    };
  }, [isActive]);
  
  /**
   * Safe state update - only update if component is still mounted
   */
  const safeSetState = useCallback((setter, value) => {
    console.log('🔍 safeSetState: mountedRef.current:', mountedRef.current, 'attempting to set value:', typeof value === 'object' ? Object.keys(value).length : value);
    if (mountedRef.current) {
      console.log('✅ safeSetState: Updating state');
      setter(value);
    } else {
      console.error('❌ safeSetState: Component unmounted, skipping state update');
    }
  }, []);
  
    /**
   * Load roster data with progressive enhancement
   */
  const loadRosterData = useCallback(async () => {
    if (!isActive) {
      console.log('useRosterData: Component is inactive, skipping data load');
      return;
    }
    
    if (loadingRef.current) {
      console.log('useRosterData: Load already in progress, skipping');
      return;
    }
    
    loadingRef.current = true;
    safeSetState(setLoading, true);
    safeSetState(setError, '');

    if (isCacheClearNeeded()) {
      rosterDataService.clearCache();
      clearStaffingCache();
      clearSpendCache();
      markCachesCleared();
    }

    try {
      console.log('📊 useRosterData: Starting progressive data loading...');
      
      // Phase 1: Load basic roster data first (highest priority)
      console.log('📊 useRosterData: Phase 1 - Loading basic roster data...');
      const rosterData = await rosterDataService.fetchRosterData(department);
      
      console.log('🔍 useRosterData: About to update state - mountedRef.current:', mountedRef.current, 'rosterData.length:', rosterData.length);
      
      // Always update state for initial data load - don't check mountedRef
      console.log('🔄 useRosterData: Setting roster state with', rosterData.length, 'members');
      setRoster(rosterData);
      setBasicRosterReady(true);
      setLoading(false); // Basic data is ready, stop main loading
      console.log('✅ useRosterData: Phase 1 complete - Basic roster ready with', rosterData.length, 'members');
      
      // Phase 2: Load supporting data in parallel (needed for allocations)
      console.log('📊 useRosterData: Phase 2 - Loading supporting data...');
      safeSetState(setEnhancementProgress, prev => ({ ...prev, supportingData: 'loading' }));
      
      const supportingData = await rosterDataService.fetchSupportingData();
      
      console.log('🔍 useRosterData: About to update supporting data - mountedRef.current:', mountedRef.current);
      
      // Always update state for supporting data - don't check mountedRef
      console.log('🔄 useRosterData: Setting supporting data states');
      setOutcomes(supportingData.outcomes);
      setApplications(supportingData.applications);
      setAvailableTags(supportingData.availableTags);
      setCompanyOptions(supportingData.companyOptions);
      setLocationOptions(supportingData.locationOptions);
      setEnhancementProgress(prev => ({ ...prev, supportingData: 'complete' }));
      console.log('✅ useRosterData: Phase 2 complete - Supporting data loaded');
      
      // Phase 3: Load outcome allocations (depends on outcomes being available)
      console.log('📊 useRosterData: Phase 3 - Loading outcome allocations...');
      safeSetState(setEnhancementProgress, prev => ({ ...prev, allocations: 'loading' }));
      
      const allocationsData = await rosterDataService.fetchOutcomeAllocations(yearRef.current);
      
      // Always update state for allocations - don't check mountedRef
      setOutcomeAllocations(allocationsData);
      setEnhancementProgress(prev => ({ ...prev, allocations: 'complete' }));
      console.log('✅ useRosterData: Phase 3 complete - Outcome allocations loaded');
      
      // Phase 4: Load unassigned roles (lowest priority)
      console.log('📊 useRosterData: Phase 4 - Loading unassigned roles...');
      safeSetState(setEnhancementProgress, prev => ({ ...prev, unassignedRoles: 'loading' }));
      
      const unassignedData = await rosterDataService.fetchUnassignedRoles(yearRef.current);
      
      // Always update state for unassigned roles - don't check mountedRef
      setUnassignedRoles(unassignedData);
      setEnhancementProgress(prev => ({ ...prev, unassignedRoles: 'complete' }));
      console.log('✅ useRosterData: Phase 4 complete - Unassigned roles loaded');
      
      console.log('🎉 useRosterData: All phases complete - Roster fully loaded');
      
    } catch (error) {
      console.error('❌ useRosterData: Error during data loading:', error);
      
      // Always update error state - don't check mountedRef
      setError(`Failed to load roster data: ${error.message}`);
      setLoading(false);
      
      // Mark failed phases
      setEnhancementProgress(prev => ({
        allocations: prev.allocations === 'loading' ? 'failed' : prev.allocations,
        unassignedRoles: prev.unassignedRoles === 'loading' ? 'failed' : prev.unassignedRoles,
        supportingData: prev.supportingData === 'loading' ? 'failed' : prev.supportingData
      }));
    } finally {
      loadingRef.current = false;
    }
  }, [safeSetState, isActive, department]);

  /**
   * Refetch specific data type
   */
  const refetch = useCallback(async (dataType = 'all') => {
    console.log('🔄 useRosterData: Refetching data type:', dataType);
    
    try {
      switch (dataType) {
        case 'roster':
          safeSetState(setLoading, true);
          const rosterData = await rosterDataService.forceRefresh('roster');
          safeSetState(setRoster, rosterData);
          safeSetState(setLoading, false);
          break;
          
        case 'allocations':
          safeSetState(setEnhancementProgress, prev => ({ ...prev, allocations: 'loading' }));
          const allocationsData = await rosterDataService.forceRefresh('allocations', yearRef.current);
          safeSetState(setOutcomeAllocations, allocationsData);
          safeSetState(setEnhancementProgress, prev => ({ ...prev, allocations: 'complete' }));
          break;
          
        case 'unassigned':
          safeSetState(setEnhancementProgress, prev => ({ ...prev, unassignedRoles: 'loading' }));
          const unassignedData = await rosterDataService.forceRefresh('unassigned', yearRef.current);
          safeSetState(setUnassignedRoles, unassignedData);
          safeSetState(setEnhancementProgress, prev => ({ ...prev, unassignedRoles: 'complete' }));
          break;
          
        case 'supporting':
          safeSetState(setEnhancementProgress, prev => ({ ...prev, supportingData: 'loading' }));
          const supportingData = await rosterDataService.forceRefresh('supporting');
          safeSetState(setOutcomes, supportingData.outcomes);
          safeSetState(setApplications, supportingData.applications);
          safeSetState(setAvailableTags, supportingData.availableTags);
          safeSetState(setCompanyOptions, supportingData.companyOptions);
          safeSetState(setLocationOptions, supportingData.locationOptions);
          safeSetState(setEnhancementProgress, prev => ({ ...prev, supportingData: 'complete' }));
          break;
          
        case 'all':
        default:
          await loadRosterData();
          break;
      }
      
      console.log('✅ useRosterData: Refetch completed for:', dataType);
      
    } catch (error) {
      console.error('❌ useRosterData: Error during refetch:', error);
      safeSetState(setError, `Failed to refresh ${dataType}: ${error.message}`);
    }
  }, [loadRosterData, safeSetState]);
  
  /**
   * Clear cache and refresh all data
   */
  const clearCacheAndRefresh = useCallback(async () => {
    console.log('🧹 useRosterData: Clearing cache and refreshing all data');
    
    // Clear service cache
    rosterDataService.clearCache();
    
    // Reset all states
    safeSetState(setRoster, []);
    safeSetState(setOutcomeAllocations, []);
    safeSetState(setUnassignedRoles, []);
    safeSetState(setOutcomes, []);
    safeSetState(setApplications, []);
    safeSetState(setAvailableTags, []);
    safeSetState(setBasicRosterReady, false);
    safeSetState(setEnhancementProgress, {
      allocations: 'pending',
      unassignedRoles: 'pending',
      supportingData: 'pending'
    });
    
    // Reload all data
    await loadRosterData();
  }, [loadRosterData, safeSetState]);
  
  /**
   * Get data loading summary
   */
  const getLoadingSummary = useCallback(() => {
    const summary = {
      basicDataReady: basicRosterReady,
      allDataReady: enhancementProgress.allocations === 'complete' && 
                    enhancementProgress.unassignedRoles === 'complete' && 
                    enhancementProgress.supportingData === 'complete',
      isLoading: loading || 
                 enhancementProgress.allocations === 'loading' || 
                 enhancementProgress.unassignedRoles === 'loading' || 
                 enhancementProgress.supportingData === 'loading',
      hasErrors: !!error || 
                 enhancementProgress.allocations === 'failed' || 
                 enhancementProgress.unassignedRoles === 'failed' || 
                 enhancementProgress.supportingData === 'failed',
      progress: enhancementProgress,
      dataCounts: {
        roster: roster.length,
        outcomeAllocations: outcomeAllocations.length,
        unassignedRoles: unassignedRoles.length,
        outcomes: outcomes.length,
        applications: applications.length,
        availableTags: availableTags.length
      }
    };
    
    return summary;
  }, [basicRosterReady, enhancementProgress, loading, error, roster.length, outcomeAllocations.length, unassignedRoles.length, outcomes.length, applications.length, availableTags.length]);
  
  /**
   * Get service statistics
   */
  const getServiceStats = useCallback(() => {
    return rosterDataService.getCacheStats();
  }, []);
  
    // Initial data load - only when component is active
  useEffect(() => {
    if (isActive) {
      console.log('📊 useRosterData: Component is active, loading data');
      loadRosterData();
    } else {
      console.log('📊 useRosterData: Component is inactive, skipping data load');
    }
  }, [loadRosterData, isActive]);
  
    // Reload data when year changes - only when component is active
  useEffect(() => {
    if (isActive && yearRef.current !== year && basicRosterReady) {
      console.log('📊 useRosterData: Year changed from', yearRef.current, 'to', year, '- reloading allocations and unassigned roles');
      
      // Only reload year-dependent data
      refetch('allocations');
      refetch('unassigned');
    }
  }, [year, basicRosterReady, refetch, isActive]);

  // Reload when global department filter changes
  useEffect(() => {
    if (isActive) {
      rosterDataService.clearCache();
      loadRosterData();
    }
  // eslint-disable-next-line
  }, [department]);

  // React to cache invalidation events fired by RatesTab after rate propagation
  useEffect(() => {
    const handleCachesInvalidated = (event) => {
      // Ignore events we dispatched ourselves (e.g. from Roster's manual refresh)
      if (event.detail?.source === 'roster_manual_refresh') return;
      clearCacheAndRefresh();
    };
    // Re-fetch allocations when StaffingStep silent refresh saves new rates for an outcome
    const handleOutcomeRatesUpdated = () => {
      if (isActive) refetch('allocations');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('outcomeCachesInvalidated', handleCachesInvalidated);
      window.addEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
      return () => {
        window.removeEventListener('outcomeCachesInvalidated', handleCachesInvalidated);
        window.removeEventListener('outcomeRatesUpdated', handleOutcomeRatesUpdated);
      };
    }
  }, [clearCacheAndRefresh, refetch, isActive]);

  const returnData = {
    // Core data
    roster,
    outcomeAllocations,
    unassignedRoles,
    outcomes,
    applications,
    availableTags,
    companyOptions,
    locationOptions,
    
    // State indicators
    loading,
    error,
    basicRosterReady,
    enhancementProgress,
    
    // Actions
    refetch,
    clearCacheAndRefresh,
    
    // Utilities
    getLoadingSummary,
    getServiceStats
  };
  
  console.log('🔍 useRosterData: Returning data:', {
    roster: roster.length,
    outcomeAllocations: outcomeAllocations.length,
    unassignedRoles: unassignedRoles.length,
    availableTags: availableTags.length,
    loading,
    basicRosterReady
  });
  
  return returnData;
}